import { SxProps, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { CustomTypographyVariant } from 'theme/theme';

type AutoTruncatingTypographyWithTooltipProps = {
  text: string;
  searchQuery?: string;
  variant: CustomTypographyVariant;
  sx?: SxProps;
  useHighlighter?: boolean;
  clampValue?: number;
};

const AutoTruncatingTypographyWithTooltip = ({
  text,
  searchQuery,
  variant,
  sx,
  useHighlighter = false,
  clampValue = 2,
}: AutoTruncatingTypographyWithTooltipProps) => {
  // Ref and state for text truncation detection
  const textRef = useRef<HTMLDivElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  // Use a separate ref to track content dimensions
  const contentRef = useRef<{ height: number; lineHeight: number }>({ height: 0, lineHeight: 0 });

  useEffect(() => {
    const el = textRef.current;

    const checkTruncation = () => {
      if (el) {
        // Create a clone of the element to measure the full height without truncation
        const clone = el.cloneNode(true) as HTMLDivElement;

        // Copy styles but remove truncation-related ones
        const computedStyle = window.getComputedStyle(el);
        const cloneStyles = {
          position: 'absolute',
          visibility: 'hidden',
          display: 'block', // Override -webkit-box
          WebkitLineClamp: 'unset',
          WebkitBoxOrient: 'unset',
          overflow: 'visible',
          height: 'auto',
          width: el.clientWidth + 'px', // Match width
          fontSize: computedStyle.fontSize,
          fontFamily: computedStyle.fontFamily,
          fontWeight: computedStyle.fontWeight,
          lineHeight: computedStyle.lineHeight,
          letterSpacing: computedStyle.letterSpacing,
        };

        // Apply styles to clone
        Object.assign(clone.style, cloneStyles);

        // Append clone to body to get accurate measurements
        document.body.appendChild(clone);

        // Compare heights - if untruncated height is greater than visible height, text is truncated
        const originalHeight = el.clientHeight;
        const fullHeight = clone.offsetHeight;

        // Remove clone after measurement
        document.body.removeChild(clone);

        // If the full height is significantly larger (to account for small rounding differences)
        // then we consider the text truncated
        const truncated = fullHeight > originalHeight + 2; // 2px buffer for rounding

        // Store dimensions for debugging if needed
        const style = window.getComputedStyle(el);
        const lineHeight = parseInt(style.lineHeight) || parseInt(style.fontSize) * 1.2;
        contentRef.current = {
          height: fullHeight,
          lineHeight,
        };

        setIsTextTruncated(truncated);
      }
    };

    // Wait a brief moment for the component to render properly
    // This is especially important for the highlighter
    const timer = setTimeout(checkTruncation, 0);

    // Add resize listener
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text, searchQuery, useHighlighter]); // Include searchQuery and useHighlighter as dependencies

  return (
    <Tooltip title={isTextTruncated ? text : ''} placement="top">
      <Typography
        variant={variant}
        ref={textRef}
        sx={{
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: clampValue,
          WebkitBoxOrient: 'vertical',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word',
          wordWrap: 'break-word',
          hyphens: 'auto',
          pr: 5,
          ...sx,
        }}
      >
        {useHighlighter && searchQuery ? (
          <Highlighter
            highlightClassName="highlight"
            searchWords={[searchQuery]}
            autoEscape={true}
            textToHighlight={text}
          />
        ) : (
          <>{text}</>
        )}
      </Typography>
    </Tooltip>
  );
};

export default AutoTruncatingTypographyWithTooltip;
